'use client';

import type { ReactNode } from 'react';
import {
  CohesionProvider,
  useMonarch,
} from '@redventures/cohesion-utils-react';
import useMonarchData from '@hen-site/app/hooks/useMonarchData';
import {
  GlobalContextProvider,
  type GlobalContextProviderProps,
} from '@hen-site/component/GlobalContextProvider';

export interface MonarchProviderWrapperProps {
  children: ReactNode;
  /** Initial globalProps built from tenant/page (domain, siteConfig, theme, etc.). */
  initialGlobalProps: GlobalContextProviderProps['globalProps'];
}

/**
 * Client wrapper that provides CohesionProvider and GlobalContextProvider
 * with Monarch ruleset (useMonarchData) and traffic flow (useMonarch).
 * Use in [tenant] layout so all pages can use useGlobalProps() and Monarch slots.
 */
function MonarchProviderContent({
  children,
  initialGlobalProps,
}: MonarchProviderWrapperProps) {
  const monarchBase = useMonarchData();
  const flow = useMonarch();
  const monarchTrafficFlow = {
    ...flow,
    preampPlacements:
      (flow as { preampPlacements?: Record<string, unknown> })
        .preampPlacements ?? {},
    preampAssets: (flow as { preampAssets?: unknown[] }).preampAssets ?? [],
  };

  return (
    <GlobalContextProvider
      globalProps={initialGlobalProps}
      monarchBase={monarchBase}
      monarchTrafficFlow={monarchTrafficFlow}
    >
      {children}
    </GlobalContextProvider>
  );
}

export function MonarchProviderWrapper({
  children,
  initialGlobalProps,
}: MonarchProviderWrapperProps) {
  return (
    <CohesionProvider>
      <MonarchProviderContent initialGlobalProps={initialGlobalProps}>
        {children}
      </MonarchProviderContent>
    </CohesionProvider>
  );
}
